import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/userSavedPopsService";

const DEFAULT_LIMIT = 12;
const MAX_LIMIT = 30;

const getValidId = (popId) => {
  const id = Number(popId);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const getErrorMessage = (error, fallback) => {
  return error?.response?.data?.message || error?.message || fallback;
};

export const useUserSavedPops = ({
  limit = DEFAULT_LIMIT,
  autoLoad = false,
} = {}) => {
  const parsedLimit = Math.min(
    Math.max(Number.parseInt(limit, 10) || DEFAULT_LIMIT, 1),
    MAX_LIMIT,
  );

  const [savedPops, setSavedPops] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const [savedPopIds, setSavedPopIds] = useState(new Set());

  const [savingPopIds, setSavingPopIds] = useState(new Set());

  const [savedStatusLoading, setSavedStatusLoading] = useState(false);

  const [saveError, setSaveError] = useState("");

  const mountedRef = useRef(false);
  const fetchingRef = useRef(false);

  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);

  const savedPopIdsRef = useRef(new Set());
  const loadedSavedStatusesRef = useRef(new Set());

  const savingRef = useRef(new Set());
  const statusRequestsRef = useRef(new Map());

  const statusVersionsRef = useRef(new Map());

  const listRequestVersionRef = useRef(0);

  const updateSavedState = useCallback((popId, saved) => {
    const id = getValidId(popId);

    if (!id) return;

    const key = String(id);

    const next = new Set(savedPopIdsRef.current);

    if (saved) {
      next.add(key);
    } else {
      next.delete(key);
    }

    savedPopIdsRef.current = next;
    loadedSavedStatusesRef.current.add(key);

    if (mountedRef.current) {
      setSavedPopIds(new Set(next));
    }
  }, []);

  const isPopSaved = useCallback(
    (popId) => {
      const id = getValidId(popId);

      return id ? savedPopIds.has(String(id)) : false;
    },
    [savedPopIds],
  );

  const isPopSaving = useCallback(
    (popId) => {
      const id = getValidId(popId);

      return id ? savingPopIds.has(String(id)) : false;
    },
    [savingPopIds],
  );

  const hasSavedStatus = useCallback((popId) => {
    const id = getValidId(popId);

    return id ? loadedSavedStatusesRef.current.has(String(id)) : false;
  }, []);

  const getPopSavedStatus = useCallback(
    async (popId, { force = false } = {}) => {
      const id = getValidId(popId);

      if (!id) return false;

      const key = String(id);

      if (savingRef.current.has(key)) {
        return savedPopIdsRef.current.has(key);
      }

      if (statusRequestsRef.current.has(key)) {
        return statusRequestsRef.current.get(key);
      }

      if (!force && loadedSavedStatusesRef.current.has(key)) {
        return savedPopIdsRef.current.has(key);
      }

      const version = statusVersionsRef.current.get(key) || 0;

      setSavedStatusLoading(true);
      setSaveError("");

      const request = (async () => {
        try {
          const result = await service.getSavedPopStatus(id);

          if (result?.success === false) {
            throw new Error(result.message || "Unable to check saved status.");
          }

          const saved = result?.saved ?? result?.isSaved;

          if (typeof saved !== "boolean") {
            throw new Error("Invalid saved status response.");
          }

          const latestVersion = statusVersionsRef.current.get(key) || 0;

          if (version === latestVersion && !savingRef.current.has(key)) {
            updateSavedState(id, saved);
          }

          return savedPopIdsRef.current.has(key);
        } catch (err) {
          console.error("getPopSavedStatus error:", err);

          if (mountedRef.current) {
            setSaveError(getErrorMessage(err, "Unable to check saved status."));
          }

          return false;
        } finally {
          statusRequestsRef.current.delete(key);

          if (mountedRef.current && statusRequestsRef.current.size === 0) {
            setSavedStatusLoading(false);
          }
        }
      })();

      statusRequestsRef.current.set(key, request);

      return request;
    },
    [updateSavedState],
  );

  const savePop = useCallback(
    async (popId) => {
      const id = getValidId(popId);

      if (!id) return false;

      const key = String(id);

      if (savingRef.current.has(key)) {
        return false;
      }

      if (
        loadedSavedStatusesRef.current.has(key) &&
        savedPopIdsRef.current.has(key)
      ) {
        return {
          success: true,
          saved: true,
        };
      }

      const wasSaved = savedPopIdsRef.current.has(key);

      savingRef.current.add(key);

      statusVersionsRef.current.set(
        key,
        (statusVersionsRef.current.get(key) || 0) + 1,
      );

      if (mountedRef.current) {
        setSavingPopIds((previous) => new Set([...previous, key]));

        setSaveError("");
      }

      try {
        const result = await service.savePop(id);

        if (result?.success !== true) {
          throw new Error(result?.message || "Unable to save Pop.");
        }

        updateSavedState(id, true);

        if (mountedRef.current && !wasSaved) {
          setTotal((previous) => previous + 1);
        }

        return result;
      } catch (err) {
        console.error("savePop error:", err);

        if (mountedRef.current) {
          setSaveError(getErrorMessage(err, "Unable to save Pop."));
        }

        return false;
      } finally {
        savingRef.current.delete(key);

        if (mountedRef.current) {
          setSavingPopIds((previous) => {
            const next = new Set(previous);
            next.delete(key);
            return next;
          });
        }
      }
    },
    [updateSavedState],
  );

  const unsavePop = useCallback(
    async (popId) => {
      const id = getValidId(popId);

      if (!id) return false;

      const key = String(id);

      if (savingRef.current.has(key)) {
        return false;
      }

      if (
        loadedSavedStatusesRef.current.has(key) &&
        !savedPopIdsRef.current.has(key)
      ) {
        return {
          success: true,
          saved: false,
        };
      }

      savingRef.current.add(key);

      statusVersionsRef.current.set(
        key,
        (statusVersionsRef.current.get(key) || 0) + 1,
      );

      if (mountedRef.current) {
        setSavingPopIds((previous) => new Set([...previous, key]));

        setSaveError("");
      }

      try {
        const result = await service.unsavePop(id);

        if (result?.success !== true) {
          throw new Error(result?.message || "Unable to unsave Pop.");
        }

        updateSavedState(id, false);

        if (mountedRef.current) {
          setSavedPops((previous) =>
            previous.filter((pop) => String(pop.popId) !== key),
          );

          setTotal((previous) => Math.max(0, previous - 1));
        }

        return result;
      } catch (err) {
        console.error("unsavePop error:", err);

        if (mountedRef.current) {
          setSaveError(getErrorMessage(err, "Unable to unsave Pop."));
        }

        return false;
      } finally {
        savingRef.current.delete(key);

        if (mountedRef.current) {
          setSavingPopIds((previous) => {
            const next = new Set(previous);
            next.delete(key);
            return next;
          });
        }
      }
    },
    [updateSavedState],
  );

  const toggleSavePop = useCallback(
    async (popId) => {
      const id = getValidId(popId);

      if (!id) return false;

      const key = String(id);

      if (savingRef.current.has(key)) {
        return false;
      }

      if (!loadedSavedStatusesRef.current.has(key)) {
        await getPopSavedStatus(id);
      }

      if (!loadedSavedStatusesRef.current.has(key)) {
        return false;
      }

      if (savingRef.current.has(key)) {
        return false;
      }

      return savedPopIdsRef.current.has(key) ? unsavePop(id) : savePop(id);
    },
    [getPopSavedStatus, savePop, unsavePop],
  );

  const getUserSavedPops = useCallback(
    async ({ nextOffset = 0, replace = false } = {}) => {
      const parsedOffset = Math.max(Number(nextOffset) || 0, 0);

      if (fetchingRef.current) {
        return false;
      }

      if (!replace && !hasMoreRef.current) {
        return false;
      }

      fetchingRef.current = true;

      const requestVersion = ++listRequestVersionRef.current;

      if (mountedRef.current) {
        if (parsedOffset === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        setError("");
      }

      try {
        const response = await service.getSavedPops({
          limit: parsedLimit,
          offset: parsedOffset,
        });

        if (response?.success === false) {
          throw new Error(response.message || "Unable to load saved Pops.");
        }

        if (
          !mountedRef.current ||
          requestVersion !== listRequestVersionRef.current
        ) {
          return false;
        }

        const incomingPops = Array.isArray(response?.pops)
          ? response.pops
          : Array.isArray(response)
            ? response
            : [];

        const pagination = response?.pagination || {};

        const nextOffsetValue = Number(
          pagination.nextOffset ?? parsedOffset + incomingPops.length,
        );

        const more =
          pagination.hasMore != null
            ? Boolean(pagination.hasMore)
            : incomingPops.length === parsedLimit;

        setSavedPops((previous) => {
          if (replace || parsedOffset === 0) {
            return incomingPops;
          }

          const existingIds = new Set(previous.map((pop) => String(pop.popId)));

          const unique = incomingPops.filter(
            (pop) => !existingIds.has(String(pop.popId)),
          );

          return [...previous, ...unique];
        });

        incomingPops.forEach((pop) => {
          const id = getValidId(pop?.popId);

          if (!id) return;

          const key = String(id);

          if (
            !savingRef.current.has(key) &&
            !loadedSavedStatusesRef.current.has(key)
          ) {
            updateSavedState(id, true);
          }
        });

        offsetRef.current = nextOffsetValue;
        hasMoreRef.current = more;

        setOffset(nextOffsetValue);
        setHasMore(more);

        if (pagination.total != null) {
          setTotal(Number(pagination.total) || 0);
        } else if (replace || parsedOffset === 0) {
          setTotal(incomingPops.length);
        }

        return response;
      } catch (err) {
        console.error("getUserSavedPops error:", err);

        if (mountedRef.current) {
          setError(getErrorMessage(err, "Unable to load saved Pops."));
        }

        return false;
      } finally {
        fetchingRef.current = false;

        if (mountedRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [parsedLimit, updateSavedState],
  );

  const loadMore = useCallback(() => {
    if (fetchingRef.current || !hasMoreRef.current) {
      return Promise.resolve(false);
    }

    return getUserSavedPops({
      nextOffset: offsetRef.current,
    });
  }, [getUserSavedPops]);

  const refresh = useCallback(() => {
    if (fetchingRef.current) {
      return Promise.resolve(false);
    }

    offsetRef.current = 0;
    hasMoreRef.current = true;

    setOffset(0);
    setHasMore(true);

    return getUserSavedPops({
      nextOffset: 0,
      replace: true,
    });
  }, [getUserSavedPops]);

  useEffect(() => {
    mountedRef.current = true;

    if (autoLoad) {
      void getUserSavedPops({
        nextOffset: 0,
        replace: true,
      });
    }

    return () => {
      mountedRef.current = false;

      listRequestVersionRef.current += 1;
    };
  }, [autoLoad, getUserSavedPops]);

  return {
    savedPops,

    loading,
    loadingMore,
    error,

    offset,
    hasMore,
    total,

    savedPopIds,
    savingPopIds,

    savedStatusLoading,
    saveError,

    getUserSavedPops,
    loadMore,
    refresh,

    isPopSaved,
    isPopSaving,
    hasSavedStatus,
    getPopSavedStatus,

    savePop,
    unsavePop,
    toggleSavePop,
  };
};

export default useUserSavedPops;
